import {
  FilterSelect,
  FilterSelectProps,
  Icon,
} from '@global-ecom/nitro-uds/elements';
import { useState } from 'react';
import { useRouter } from 'next/router';

import { useTranslate } from 'hooks/useTranslations';
import { Container } from 'ui/elements/LayoutContainer';
import { isNotNullish } from 'utils/isNotNullish';

import { GlossaryItem } from './GlossaryItem';

export type GlossaryFiltersProps = {
  terms: GlossaryItem[];
  filteredTerms: GlossaryItem[];
  setFilteredTerms: (terms: GlossaryItem[]) => void;
};

export function GlossaryFilters(props: GlossaryFiltersProps) {
  const t = useTranslate();
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState('');

  const allTags = props.terms.reduce(
    (acc, item) => [...acc, ...(item.tags ?? [])],
    [] as FilterSelectProps['options']
  );

  const allFirstLetters = props.filteredTerms
    .reduce((acc, item) => [...acc, item.term.slice(0, 1)], [] as string[])
    .filter(isNotNullish)
    .sort();

  const allFirstLettersUnique = [...new Set(allFirstLetters)] as string[];

  const allTagsUnique = new Map(allTags.map(tag => [tag.value, tag.label]));
  const glossaryCategoryOptions = Array.from(allTagsUnique)
    .sort()
    .map(([key, value]) => ({
      value: key,
      label: value,
    }));

  return (
    <Container bleed={true} id="glossary-filters" className="border-t border-b">
      <nav className="flex justify-between mx-auto w-full max-w-screen-3xl mobile:p-4 tablet:p-6 desktop:p-6">
        <div className="flex items-center">
          <h2 className="mr-3">
            <span className="sr-only">{t('filters')}</span>
            <Icon name="filter" size="xl" />
          </h2>
          <div>
            <FilterSelect
              value={selectedCategory}
              dataTestId="glossary-category-select"
              id="glossary-category-select"
              options={[
                ...glossaryCategoryOptions,
                {
                  value: 'all',
                  label: t('viewAll'),
                },
              ]}
              label={t('category')}
              name="glossaryCategorySelect"
              onChange={value => {
                setSelectedCategory(value.toString());
                props.setFilteredTerms(
                  value === 'all'
                    ? props.terms
                    : props.terms.filter(term =>
                        term.tags?.find(tag => tag.value === value)
                      )
                );
              }}
              placeholder={t('category')}
            />
          </div>
        </div>
        <div>
          <FilterSelect
            dataTestId="glossary-letter-select"
            id="glossary-letter-select"
            options={allFirstLettersUnique?.map(item => ({
              value: item,
              label: item.toUpperCase(),
            }))}
            label={t('aToZ')}
            name="glossaryJumpToLetterSelect"
            onChange={value =>
              router.push('#' + value.toString().toLowerCase())
            }
            placeholder={t('aToZ')}
          />
        </div>
      </nav>
    </Container>
  );
}
